'use client';

import React, { useState, useEffect } from 'react';
import { BookingsHeader } from '@/components/dashboard/bookings/BookingsHeader';
import { BookingsKpiStrip } from '@/components/dashboard/bookings/BookingsKpiStrip';
import { BookingsAgendaView } from '@/components/dashboard/bookings/BookingsAgendaView';
import { BookingsListView } from '@/components/dashboard/bookings/BookingsListView';
import { BookingsMonthView } from '@/components/dashboard/bookings/BookingsMonthView';
import { BookingDrawer } from '@/components/dashboard/bookings/BookingDrawer';
import { AppointmentDetailDrawer } from '@/components/dashboard/bookings/AppointmentDetailDrawer';
import { 
  Appointment, 
  Staff, 
  ServiceItem, 
  BookingViewMode, 
  ChannelSummary 
} from '@/components/dashboard/bookings/types';

export default function BookingsCalendarPage() {
  const [viewMode, setViewMode] = useState<BookingViewMode>('agenda');
  const [selectedDate, setSelectedDate] = useState<string>(() => new Date().toISOString().split('T')[0]);

  // Datos del backend
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Finanzas reales
  const [channelSummary, setChannelSummary] = useState<ChannelSummary>({
    EFECTIVO: 0,
    CREDITO: 0,
    DEBITO: 0,
    TRANSFERENCIA: 0,
    MERCADOPAGO: 0,
    OTRO: 0,
    SENAS: 0,
  });
  const [totalCollected, setTotalCollected] = useState(0);

  // Modales
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // Cargar datos
  const loadData = async () => {
    setLoading(true);
    try {
      const [resApp, resStaff, resSrv, resSales] = await Promise.all([
        fetch('/api/bookings'),
        fetch('/api/staff'),
        fetch('/api/services'),
        fetch('/api/sales'),
      ]);

      const [dataApp, dataStaff, dataSrv, dataSales] = await Promise.all([
        resApp.json(),
        resStaff.json(),
        resSrv.json(),
        resSales.json(),
      ]);

      const appts: Appointment[] = dataApp.success ? dataApp.appointments || [] : [];
      setAppointments(appts);

      if (dataStaff.success) {
        setStaffList(dataStaff.staff || []);
      }
      if (dataSrv.success) {
        setServices(dataSrv.services || []);
      }

      // Cálculo financiero real
      const salesChannels = dataSales.success ? dataSales.channelSummary || {} : {};
      const salesTotal = dataSales.success ? dataSales.grandTotal || 0 : 0;

      // Calcular señas de turnos confirmados
      const totalDeposits = appts.reduce((acc, a) => acc + (a.depositAmount || 0), 0);

      setChannelSummary({
        EFECTIVO: salesChannels.EFECTIVO || 0,
        CREDITO: salesChannels.CREDITO || 0,
        DEBITO: salesChannels.DEBITO || 0,
        TRANSFERENCIA: salesChannels.TRANSFERENCIA || 0,
        MERCADOPAGO: salesChannels.MERCADOPAGO || 0,
        OTRO: salesChannels.OTRO || 0,
        SENAS: totalDeposits,
      });

      setTotalCollected(salesTotal + totalDeposits);
    } catch (e) {
      console.error('Error cargando agenda administrativa:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // Sincronización automática periódica (cada 20s) y al volver a la pestaña
    const interval = setInterval(loadData, 20000);
    const handleFocus = () => loadData();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Actualizar estado del turno en PostgreSQL
  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      const res = await fetch(`/api/bookings/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setAppointments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
        );
      }
    } catch (e) {
      console.error('Error actualizando turno:', e);
    }
  };

  // Clic en slot vacío de la agenda
  const handleSlotClick = (staffId: string, time: string) => {
    setShowNewModal(true);
  };

  const [isAttendanceView, setIsAttendanceView] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isAsistencia = window.location.search.includes('view=asistencia');
      if (isAsistencia) {
        setIsAttendanceView(true);
        setViewMode('lista');
      }
    }
  }, []);

  // Filtrar citas del día para la tira de KPIs
  const dayAppointments = appointments.filter((a) => a.dateStr === selectedDate);
  const attendedCount = dayAppointments.filter(a => a.status.toLowerCase() === 'completed').length;
  const noShowCount = dayAppointments.filter(a => a.status.toLowerCase() === 'no_show').length;
  const pendingCount = dayAppointments.filter(a => a.status.toLowerCase() === 'confirmed').length;

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] flex flex-col font-sans">
      {/* 1. Cabecera de la Agenda con Navegación de Días y Selector de Modo */}
      <BookingsHeader
        viewMode={viewMode}
        onChangeViewMode={setViewMode}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        onOpenNewBookingModal={() => setShowNewModal(true)}
      />

      {/* Banner de Modo Asistencia si se ingresó desde Gestión > Asistencia */}
      {isAttendanceView && (
        <div className="mx-4 sm:mx-6 mt-3 p-4 bg-emerald-950 text-white rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm border border-emerald-800">
          <div className="space-y-0.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-900/80 px-2.5 py-0.5 rounded-full inline-block">
              Módulo de Asistencia Activo
            </span>
            <h3 className="text-sm font-bold text-white">
              Control de Asistencia del Día ({selectedDate})
            </h3>
            <p className="text-xs text-slate-300">
              Registrá el presentismo de cada cliente. Cada cambio actualiza las métricas y queda trazado en el historial de auditoría.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="px-3 py-1.5 rounded-xl bg-white/10 text-xs font-bold text-emerald-300 border border-white/10">
              ✓ {attendedCount} Asistieron
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-white/10 text-xs font-bold text-rose-300 border border-white/10">
              ✕ {noShowCount} No vinieron
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-white/10 text-xs font-bold text-sky-300 border border-white/10">
              ⏳ {pendingCount} Por atender
            </div>
          </div>
        </div>
      )}

      {/* 2. Tira de KPIs y Tarjeta Financiera de 7 Canales con Toggle de Ojo */}
      <BookingsKpiStrip
        appointments={dayAppointments}
        channelSummary={channelSummary}
        totalCollected={totalCollected}
      />

      {/* 3. Vistas Especializadas */}
      <div className="flex-1">
        {loading ? (
          <div className="py-24 flex flex-col items-center justify-center text-slate-400 gap-2">
            <div className="w-8 h-8 border-3 border-slate-300 border-t-[#047857] rounded-full animate-spin" />
            <p className="text-xs font-semibold">Cargando agenda en tiempo real...</p>
          </div>
        ) : (
          <>
            {viewMode === 'agenda' && (
              <BookingsAgendaView
                staffList={staffList}
                appointments={appointments}
                selectedDate={selectedDate}
                onSelectAppointment={setSelectedAppointment}
                onSlotClick={handleSlotClick}
                onUpdateStatus={handleUpdateStatus}
              />
            )}

            {viewMode === 'lista' && (
              <BookingsListView
                appointments={appointments}
                selectedDate={selectedDate}
                onSelectAppointment={setSelectedAppointment}
                onUpdateStatus={handleUpdateStatus}
              />
            )}

            {viewMode === 'mes' && (
              <BookingsMonthView
                appointments={appointments}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                onGoToAgenda={() => setViewMode('agenda')}
              />
            )}
          </>
        )}
      </div>

      {/* Slide-Over Drawer: Nuevo Turno */}
      <BookingDrawer
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        staffList={staffList}
        services={services}
        selectedDate={selectedDate}
        onSuccess={loadData}
      />

      {/* Slide-Over Drawer: Detalle de Turno */}
      <AppointmentDetailDrawer
        appointment={selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
        onUpdateStatus={handleUpdateStatus}
      />
    </div>
  );
}
